import React from "react";
import { useTranslation } from "react-i18next";
import { getMemberInfo } from "@/hooks/useMembers";
import MAvatar from "@/components/ui/atoms/MAvatar";
import ActionCard from "./components/ActionCard";
import DashboardQuickActions from "./components/DashboardQuickActions";
import FeatureCards, { FEATURE_CARD_KEYS } from "./components/FeatureCards";
import FamilyWidget from "./components/FamilyWidget";
import FamilyOverview from "./components/FamilyOverview";
import UpcomingReminders from "./components/UpcomingReminders";
import ExploreMaatraGrid from "./components/ExploreMaatraGrid";
import ExploreMaatraBanner from "./components/ExploreMaatraBanner";
import DocumentSend from "./components/DocumentSend";
import DashboardSkeleton from "./components/DashboardSkeleton";
import MRevealSection from "@/components/ui/atoms/MRevealSection";
import { IconBell } from "@tabler/icons-react";
import { remindersService } from "@/services/reminders.service";
import { UpcomingFamilyReminder } from "@/types/reminders";
import styles from "./dashboard.module.css";

import { useNavigate } from "react-router-dom";
import { useYearWindow } from "@/hooks/useYearWindow";
import { useAvatar } from "@/hooks/useAvatar";
import { useFamilyStats } from "@/hooks/useFamilyStats";
import {
  useDynamicDashboardMessage,
  getTimeBasedGreetingPeriod,
} from "@/utils/dashboardMessages";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MFab from "@/components/ui/atoms/MFab";
import { MTourTrigger } from "@/components/ui/organisms/MTourTrigger";
import { MHelpIcon } from "@/components/ui/atoms/MHelpIcon";
import { ProfileStrength } from "@/types/familyStats";
import { useActionNotifications } from "@/hooks/useActionNotifications";
import { Nudge } from "./components/Nudge";
import { useDismissibleSet, DISMISS_KEYS } from "@/hooks/useDismissible";

const YEARS_WINDOW = 5;

import { useNotesContext } from "@/context/NotesContext";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { clearSelectedMember } from "@/store/authSlice";
import { useSubscription } from "@/hooks/useSubscription";
import SubscriptionExpired from "./components/SubscriptionExpired";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const Dashboard = () => {
  const { t } = useTranslation();
  const tourTriggerRef = React.useRef<any>(null);
  const dispatch = useAppDispatch();
  const { subscription, fetchCurrentSubscription } = useSubscription();

  // Clear selectedMember IMMEDIATELY before any other operations
  React.useEffect(() => {
    dispatch(clearSelectedMember());
  }, [dispatch]);

  // Always use logged-in member for dashboard (ignore selectedMember)
  const memberInfo = getMemberInfo(true);
  const memberId = memberInfo.id;
  const { actions } = useActionNotifications();
  // Check for uncategorized documents to determine if spacer should show
  const [uncategorizedCount, setUncategorizedCount] = React.useState(0);

  React.useEffect(() => {
    const checkUncategorizedDocuments = async () => {
      try {
        const { documentService } = await import(
          "@/services/documents.service"
        );
        const docService = documentService(memberId);
        const response = (await docService.getDocumentTypeCounts({
          mode: "dashboard",
        })) as { uncategorized?: number };
        setUncategorizedCount(response?.uncategorized || 0);
      } catch (error) {
        console.error("Error checking uncategorized documents:", error);
        setUncategorizedCount(0);
      }
    };

    if (memberId) {
      checkUncategorizedDocuments();
    }
  }, [memberId]);

  // Fetch subscription status for all logged-in members
  React.useEffect(() => {
    if (memberId) {
      fetchCurrentSubscription();
    }
  }, [memberId, fetchCurrentSubscription]);

  // Track GTM user_data_loaded event for home screen
  React.useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.HOMEPAGE,
      screenName: ANALYTICS_SCREEN_NAMES.HOME,
    });
  }, [memberInfo?.id]);

  const { avatarUrl } = useAvatar();
  const navigate = useNavigate();

  // Promo cards below the quick actions — each is dismissed independently;
  // when one is dismissed/clicked the other remains (and renders full width).
  const { dismissed: dismissedFeatureCards, dismiss: dismissFeatureCard } =
    useDismissibleSet(DISMISS_KEYS.dashboardFeatureCards);
  const anyFeatureCardVisible = FEATURE_CARD_KEYS.some(
    (key) => !dismissedFeatureCards.includes(key)
  );

  // Family stats for dynamic messaging + the "Family overview" section
  const {
    familyStats,
    memberStats,
    loading: memberStatsLoading,
    fetched: familyStatsFetched,
    fetchFamilyStats,
    fetchMemberStats,
  } = useFamilyStats({ memberInfo });

  const isEffectivelyExperienced =
    memberInfo.isExperienced || !!memberStats?.healthSummaryReady;

  // Tracks the "Your family" widget's own async fetch (family members list),
  // so the whole widgets block can stay skeleton-ed until it's ready too —
  // otherwise it pops in late, after everything else has already rendered.
  const [familyWidgetLoading, setFamilyWidgetLoading] = React.useState(true);

  // All the data the widgets block needs before it's safe to reveal —
  // avoids sections popping in one at a time as their own fetches resolve.
  const dashboardReady =
    !!memberStats &&
    !familyWidgetLoading &&
    (!isEffectivelyExperienced || familyStatsFetched);

  // Upcoming reminders across the family, for experienced users only
  const [upcomingReminders, setUpcomingReminders] = React.useState<
    UpcomingFamilyReminder[]
  >([]);
  React.useEffect(() => {
    if (!memberId || !isEffectivelyExperienced) return;
    remindersService
      .getUpcomingForFamily(memberId)
      .then(setUpcomingReminders)
      .catch(() => setUpcomingReminders([]));
  }, [memberId, isEffectivelyExperienced]);

  // Fetch family-wide stats (records/emergency-cards/vitals/pillbox/etc.
  // counts) for the "Family overview" section, experienced users only
  React.useEffect(() => {
    if (!memberId || !isEffectivelyExperienced) return;
    fetchFamilyStats();
  }, [memberId, isEffectivelyExperienced, fetchFamilyStats]);

  // Get profile strength with fallback to LOW
  const profileStrength = memberStats?.profileStrength || ProfileStrength.LOW;

  // Get dynamic dashboard message
  const { message: dynamicMessage, messageKey } = useDynamicDashboardMessage(
    profileStrength,
    memberInfo.firstName || ""
  );

  // Fetch unread notes count from context on dashboard mount
  const { fetchUnreadCount } = useNotesContext();
  React.useEffect(() => {
    localStorage.removeItem("documentFilters");
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  // Refetch dashboard data when selected member changes
  React.useEffect(() => {
    if (memberId) {
      fetchMemberStats(memberId);
    }
  }, [memberId, fetchMemberStats]);

  const { selectedYear } = useYearWindow(
    [],
    0,
    undefined,
    YEARS_WINDOW,
    { fetchSummary: () => {} },
    memberInfo.dobYear || 1940
  );

  // Persist selectedYear to sessionStorage to sync with DashboardDetail
  React.useEffect(() => {
    if (selectedYear) {
      sessionStorage.setItem(
        "dashboardDetailSelectedYear",
        String(selectedYear)
      );
    }
  }, [selectedYear]);

  // Check subscription status for all logged-in members
  if (subscription) {
    const subscriptionStatus = subscription.status?.toLowerCase();
    const today = new Date();
    const endDate = new Date(subscription.endDate);
    const isPastEndDate = today > endDate;
    // Show SubscriptionExpired if status is 'expired' OR if status is 'cancelled' AND past end date
    if (
      subscriptionStatus === "expired" ||
      (subscriptionStatus === "cancelled" && isPastEndDate)
    ) {
      return <SubscriptionExpired />;
    }
  }

  return (
    <div className="min-h-screen flex flex-col px-6">
      <div className="dashboard-ellipses">
        <div className="dashboard-ellipse dashboard-ellipse-1"></div>
        <div className="dashboard-ellipse dashboard-ellipse-2"></div>
        <div className="dashboard-ellipse dashboard-ellipse-3"></div>
        <div className="dashboard-ellipse dashboard-ellipse-4"></div>
        <div className="dashboard-ellipse dashboard-ellipse-5"></div>
      </div>
      {/* Top Profile Row */}
      <div className={styles.dashboardProfileRow}>
        <div className={styles.headerHelpIcon} data-wizard-help-icon>
          <MHelpIcon onClick={() => tourTriggerRef.current?.open()} />
        </div>
        <div className={styles.dashboardHeaderActions}>
          <button
            type="button"
            className={styles.notificationBellBtn}
            onClick={() => navigate("/dashboard/updates")}
            aria-label={t("dashboard.notifications")}
            data-dashboard-bell
          >
            <IconBell size={20} stroke={1.5} color="#45168C" />
          </button>
          <button
            onClick={() => navigate("/profile/")}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            data-profile-avatar
          >
            <MAvatar
              size="md"
              text={`${memberInfo.firstName || ""}${
                memberInfo.lastName ? " " + memberInfo.lastName : ""
              }`.trim()}
              type={memberInfo.avatar ? "dp" : "no-dp"}
              src={avatarUrl || undefined}
              alt={`${memberInfo.firstName || ""} ${
                memberInfo.lastName || ""
              }`.trim()}
              memberId={memberInfo.id}
            />
          </button>
        </div>
      </div>
      <div className={styles.dashboardIntro} data-dashboard-welcome>
        {isEffectivelyExperienced
          ? t(`dashboard.greetingPeriods.${getTimeBasedGreetingPeriod()}`)
          : t("dashboard.welcomeGreeting")}
        , {memberInfo.firstName || ""}
        <br />
        <span
          key={messageKey}
          className={`${styles.dashboardIntroMessage} ${
            messageKey > 0 ? styles.messageTransition : ""
          }`}
        >
          {dynamicMessage}
        </span>
      </div>
      {/* Dashboard widgets */}
      <MSpacer mt={16} />
      <div
        className={`${styles.dashboardWidgetsContainer} ${
          isEffectivelyExperienced
            ? ""
            : styles.dashboardWidgetsContainerNewUser
        }`}
      >
        {!dashboardReady && <DashboardSkeleton />}
        {/* FamilyWidget stays mounted (just hidden) even while the skeleton
            shows, so its own family-members fetch can report readiness back
            up via onLoadingChange — see dashboardReady above. */}
        <div
          className={
            dashboardReady ? "flex w-full flex-col items-center" : "hidden"
          }
        >
          {!memberStatsLoading && memberStats && (
            <MRevealSection className="w-full" delay={0}>
              <DashboardQuickActions
                onAddHealthMemory={() =>
                  navigate("/documents/health-memory", {
                    state: { backUrl: "/dashboard" },
                  })
                }
                onAddDocument={() =>
                  navigate("/documents", { state: { backUrl: "/dashboard" } })
                }
                onAddFamilyNote={() =>
                  navigate("/profile/family/notes", {
                    state: { backUrl: "/dashboard" },
                  })
                }
                healthSummaryReady={memberStats.healthSummaryReady}
                emergencyCardReady={memberStats.emergencyCardReady}
              />
            </MRevealSection>
          )}
          {((actions && actions.length > 0) || uncategorizedCount > 0) && (
            <MSpacer mt={24} />
          )}
          <MRevealSection className="w-full" delay={0.05}>
            <ActionCard
              uncategorizedCount={uncategorizedCount}
              isExperienced={isEffectivelyExperienced}
            />
          </MRevealSection>
          {isEffectivelyExperienced && anyFeatureCardVisible && (
            <>
              <MSpacer mt={48} />
              <MRevealSection className="w-full" delay={0.1}>
                <FeatureCards
                  dismissed={dismissedFeatureCards}
                  onDismiss={dismissFeatureCard}
                />
              </MRevealSection>
              <MSpacer mt={24} />
            </>
          )}
          {isEffectivelyExperienced &&
          !anyFeatureCardVisible &&
          ((actions && actions.length > 0) || uncategorizedCount > 0) ? (
            <MSpacer mt={24} />
          ) : null}
          {isEffectivelyExperienced && (
            <MRevealSection className="w-full" delay={0.1}>
              <Nudge />
            </MRevealSection>
          )}
          <MSpacer mt={20} />
          <MRevealSection className="w-full" delay={0.15}>
            <FamilyWidget
              isExperienced={isEffectivelyExperienced}
              onLoadingChange={setFamilyWidgetLoading}
            />
          </MRevealSection>
          <MSpacer mt={24} />
          {!isEffectivelyExperienced && (
            <>
              <MRevealSection className="w-full" delay={0.2}>
                <ExploreMaatraGrid />
              </MRevealSection>
              <MSpacer mt={24} />
            </>
          )}
          {isEffectivelyExperienced && familyStats && (
            <>
              <MRevealSection className="w-full" delay={0.2}>
                <FamilyOverview familyStats={familyStats} />
              </MRevealSection>
              <MSpacer mt={24} />
            </>
          )}
          {isEffectivelyExperienced && upcomingReminders.length > 0 && (
            <>
              <MRevealSection className="w-full" delay={0.25}>
                <UpcomingReminders reminders={upcomingReminders} />
              </MRevealSection>
              <MSpacer mt={24} />
            </>
          )}
          <MRevealSection className="w-full" delay={0.3}>
            <DocumentSend isExperienced={isEffectivelyExperienced} />
          </MRevealSection>
          {isEffectivelyExperienced && (
            <>
              <MSpacer mt={24} />
              <MRevealSection className="w-full" delay={0.35}>
                <ExploreMaatraBanner />
              </MRevealSection>
            </>
          )}
          <MSpacer mt={60} />
        </div>
      </div>
      <MFab />
      <MTourTrigger
        ref={tourTriggerRef}
        page={
          isEffectivelyExperienced
            ? "dashboard-experienced"
            : "dashboard-inexperienced"
        }
      />
    </div>
  );
};

export default Dashboard;
